import React, { lazy, Suspense, useState, useEffect } from "react"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import { useField } from "formik"
import { autoPlacement, offset } from "@floating-ui/dom"
import "react-datepicker/dist/react-datepicker.css"

import {
  InputContainer,
  InputFieldLabel,
  InputFieldIcon,
  ErrorMessage,
  DisabledMessage,
} from "./common"

import { InputField } from "./Input"

import { mixins } from "../../styles"

import CheckmarkSVG from "../../images/icons/check--small.inline.svg"
import CrossSVG from "../../images/icons/cross--small.inline.svg"

const DatePickerComponent = lazy(() => import("react-datepicker"))

const DatePickerStyleWrapper = styled.div`
  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker-popper {
    padding: 0;
    z-index: 999;
  }

  .react-datepicker {
    ${mixins.copy5}
    border: none;
    border-radius: 0;
    box-shadow:
      0 1px 2px 0 rgba(0, 0, 0, 0.1),
      0 1px 1px 0 rgba(0, 0, 0, 0.05);
    color: ${({ theme }) => theme.spaceblue};
    padding: 5px 10px;
  }

  .react-datepicker__navigation {
    top: 15px;
  }

  .react-datepicker__day--disabled,
  .react-datepicker__month-text--disabled,
  .react-datepicker__quarter-text--disabled {
    color: ${({ theme }) => theme.sage};
  }

  .react-datepicker__current-month,
  .react-datepicker-time__header,
  .react-datepicker-year-header {
    color: inherit;
    font-size: inherit;
    font-weight: 700;
    margin-bottom: 15px;
  }

  .react-datepicker__day-name,
  .react-datepicker__day,
  .react-datepicker__time-name {
    align-items: center;
    display: inline-flex;
    height: 35px;
    justify-content: center;
    line-height: 1;
    margin: 1.5px;
    text-align: center;
    width: 35px;
  }

  .react-datepicker__day-name,
  .react-datepicker__time-name {
    font-weight: 700;
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day--in-range,
  .react-datepicker__month-text--selected,
  .react-datepicker__month-text--in-selecting-range,
  .react-datepicker__month-text--in-range,
  .react-datepicker__quarter-text--selected,
  .react-datepicker__quarter-text--in-selecting-range,
  .react-datepicker__quarter-text--in-range,
  .react-datepicker__day--keyboard-selected,
  .react-datepicker__month-text--keyboard-selected,
  .react-datepicker__quarter-text--keyboard-selected {
    background: ${({ theme }) => theme.greyWarm};
    border-radius: 0;
    color: ${({ theme }) => theme.spaceblue};
  }

  .react-datepicker__day:hover,
  .react-datepicker__month-text:hover,
  .react-datepicker__quarter-text:hover {
    background: ${({ theme }) => theme.greyLight};
    border-radius: 0;
  }

  .react-datepicker__triangle {
    display: none;
  }

  .react-datepicker-popper[data-placement^="bottom"] {
    border-top: 1px solid ${({ theme }) => theme.spaceblue};
  }

  .react-datepicker__header {
    background: transparent;
    border: none;
    border-radius: 0;
  }
`

export const DatePickerField = styled(DatePickerComponent)`
  appearance: none;
  background: transparent;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.sage};
  border-radius: 0;
  display: block;
  font-size: inherit;
  line-height: inherit;
  padding: 5px 20px 5px 0;
  width: 100%;

  ${(props) =>
    props.$error &&
    css`
      border-color: ${({ theme }) => theme.error};
    `}

  &:focus {
    border-color: ${({ theme }) => theme.sun};
    outline: none;
  }

  ::placeholder {
    color: ${({ theme }) => theme.sage};
    opacity: 0.75;
  }
`

const DatePicker = ({
  className,
  dateFormat,
  disabled,
  forwardRef,
  hidden,
  hideLabel,
  hideRequired,
  label,
  placeholder,
  onBlur,
  onFocus,
  required,
  ...props
}) => {
  const [field, meta, helpers] = useField(props)
  const [active, setActive] = useState(false)
  const [ok, setOk] = useState(false)

  // test field.value with datePropType
  datePropType(field.value, "value", "DatePicker")

  useEffect(() => {
    if (field.value || placeholder) {
      setActive(true)
    }

    if (!field.value || meta.error) {
      setOk(false)
    } else {
      setOk(true)
    }
  }, [field.value, meta.error, placeholder])

  const handleFocus = (callback) => {
    setActive(true)
    setOk(false)
    callback && callback()
  }

  const handleBlur = (callback) => {
    if (!field.value && !placeholder) {
      setActive(false)
    }

    if (field.value && !meta.error) {
      setOk(true)
    }
    callback && callback()
  }

  return (
    <InputContainer
      className={className}
      data-testid={`input-${field.name}`}
      hidden={hidden}
    >
      <InputFieldLabel
        $active={active}
        htmlFor={props.id || field.name}
        hidden={hideLabel}
      >
        {label} {required && !hideRequired && "*"}
      </InputFieldLabel>
      <DatePickerStyleWrapper>
        <Suspense
          fallback={
            <InputField
              {...field}
              {...props}
              dateFormatCalendar={undefined}
              disabled={disabled}
              dropdownMode={undefined}
              $error={meta.touched && meta.error ? "true" : undefined}
              id={props.id || field.name}
              maxDate={undefined}
              onBlur={() => handleBlur(onBlur)}
              onFocus={() => handleFocus(onFocus)}
              placeholder={
                placeholder &&
                `${placeholder}${
                  required && hideLabel && !hideRequired ? " *" : ""
                }`
              }
              ref={forwardRef}
              showYearDropdown={undefined}
            />
          }
        >
          <DatePickerField
            {...field}
            {...props}
            dateFormat={
              dateFormat || [
                "dd.MM.yyyy",
                "MMMM d, yyyy",
                "dd.MM",
                "dd.MM.yy",
                "d.M",
                "d.M.yy",
                "d.M.yyyy",
                "dd/MM",
                "dd/MM/yy",
                "dd/MM/yyyy",
                "d/M",
                "d/M/yy",
                "d/M/yyyy",
              ]
            }
            disabled={disabled}
            $error={meta.touched && meta.error ? "true" : undefined}
            id={props.id || field.name}
            onFocus={() => handleFocus(onFocus)}
            onBlur={() => handleBlur(onBlur)}
            onChange={(value) => {
              helpers.setValue(value)
            }}
            placeholderText={
              placeholder &&
              `${placeholder}${
                required && hideLabel && !hideRequired ? " *" : ""
              }`
            }
            popperModifiers={[
              autoPlacement({
                alignment: "bottom-start",
                autoAlignment: false,
              }),
              offset({ mainAxis: -11, crossAxis: 0 }), // overlay the bottom border of the input
            ]}
            ref={forwardRef}
            selected={field.value}
            value={null} // override Formik, ReactDatePicker doesn't like `value`
          />
        </Suspense>
      </DatePickerStyleWrapper>
      {ok && (
        <InputFieldIcon data-testid="checkmark">
          <CheckmarkSVG />
        </InputFieldIcon>
      )}
      {meta.touched && meta.error ? (
        <>
          {meta.error && (
            <InputFieldIcon $error data-testid="errorcross">
              <CrossSVG />
            </InputFieldIcon>
          )}
          <ErrorMessage>
            {meta.error}
            {disabled && <DisabledMessage>{disabled}</DisabledMessage>}
          </ErrorMessage>
        </>
      ) : null}
      {disabled && !meta.error && <DisabledMessage>{disabled}</DisabledMessage>}
    </InputContainer>
  )
}

const datePropType = (prop, propName, componentName) => {
  if (prop && !(prop instanceof Date)) {
    console.warn(
      `Invalid prop \`${propName}\` supplied to` +
        ` \`${componentName}\`. Expected \`Date\` type, was \`${typeof prop}\`.`
    )
  }
}

DatePicker.propTypes = {
  className: PropTypes.string,
  dateFormat: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  forwardRef: PropTypes.object,
  hidden: PropTypes.bool,
  hideRequired: PropTypes.bool,
  id: PropTypes.string,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
}

export default DatePicker
